import React, { useState, useEffect } from 'react';

const LogoManagement = () => {
  const [logoSettings, setLogoSettings] = useState({
    mainLogo: '/assets/logos/logo-fallback.svg',
    blueLogo: '/assets/logos/logo-fallback.svg',
    favicon: '/favicon.ico'
  });

  const [previews, setPreviews] = useState({});

  useEffect(() => {
    // Load saved settings from localStorage
    const saved = localStorage.getItem('adminLogoSettings');
    console.log('Loading logo settings:', saved);
    
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        console.log('Parsed logo settings:', parsedSettings);
        setLogoSettings(parsedSettings);
      } catch (error) {
        console.error('Error parsing adminLogoSettings:', error);
      }
    } else {
      // Check if Header format exists and convert
      const headerLogos = localStorage.getItem('adminLogos');
      console.log('Checking header logos:', headerLogos);
      
      if (headerLogos) {
        try {
          const parsed = JSON.parse(headerLogos);
          console.log('Converting header logos:', parsed);
          const convertedSettings = {
            mainLogo: parsed.main || '/assets/logos/logo-fallback.svg',
            blueLogo: parsed.blue || '/assets/logos/logo-fallback.svg',
            favicon: parsed.favicon || '/favicon.ico'
          };
          setLogoSettings(convertedSettings);
          
          // Save in the new format
          localStorage.setItem('adminLogoSettings', JSON.stringify(convertedSettings));
        } catch (error) {
          console.error('Error parsing adminLogos:', error);
        }
      }
    }
  }, []);

  const handleFileUpload = (e, logoType) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        setPreviews(prev => ({
          ...prev,
          [logoType]: dataUrl
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    // Update logo settings with new previews if available
    const updatedSettings = { ...logoSettings };
    Object.keys(previews).forEach(key => {
      if (previews[key]) {
        updatedSettings[key] = previews[key];
      }
    });

    setLogoSettings(updatedSettings);
    localStorage.setItem('adminLogoSettings', JSON.stringify(updatedSettings));
    
    // Also save in format expected by Header component
    const headerLogos = {
      main: updatedSettings.mainLogo,
      blue: updatedSettings.blueLogo,
      favicon: updatedSettings.favicon
    };
    localStorage.setItem('adminLogos', JSON.stringify(headerLogos));
    
    // Update favicon immediately
    if (updatedSettings.favicon && updatedSettings.favicon !== '/favicon.ico') {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = updatedSettings.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    
    // Trigger a custom event to update Header immediately
    window.dispatchEvent(new CustomEvent('logoUpdated'));
    
    // Clear previews after saving
    setPreviews({});
    
    // Show success message with instructions
    const message = `✅ Logo settings saved successfully!\n\n` +
      `📋 What happens next:\n` +
      `• Your new logo is now active on the website\n` +
      `• Navigate back to the main website to see the changes\n` +
      `• The logo will appear in the header navigation\n` +
      `• Favicon will update in the browser tab\n\n` +
      `💡 Tip: Open your main website in a new tab to see the updated logo!`;
    
    alert(message);
  };

  const handleReset = (logoType) => {
    setPreviews(prev => ({
      ...prev,
      [logoType]: null
    }));
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Logo Management</h2>
        <p>Upload and manage your website logos and favicon</p>
      </div>

      <div className="upload-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '30px',
        marginBottom: '40px'
      }}>
        {/* Main Logo */}
        <div className="upload-section" style={{
          background: '#f8f9fa',
          borderRadius: '12px',
          padding: '25px',
          border: '2px solid #e9ecef',
          textAlign: 'center',
          transition: 'all 0.3s ease'
        }}>
          <h3 style={{
            color: '#2652a0',
            marginBottom: '15px',
            fontSize: '1.2rem',
            fontWeight: '600'
          }}>Main Logo (White)</h3>
          <p style={{
            color: '#6c757d',
            marginBottom: '20px',
            fontSize: '14px'
          }}>Used in the header and dark backgrounds</p>
          
          <div className="upload-area" style={{
            marginBottom: '20px'
          }}>
            <div className="upload-placeholder" style={{
              width: '150px',
              height: '80px',
              background: '#2652a0',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 15px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {previews.mainLogo || (logoSettings.mainLogo && logoSettings.mainLogo !== '/assets/logos/logo-fallback.svg') ? (
                <img 
                  src={previews.mainLogo || logoSettings.mainLogo} 
                  alt="Main Logo Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <div style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#ffdd00', marginRight: '5px' }}>〜</span>
                  <span style={{ color: '#ffffff' }}>River</span>
                  <span style={{ color: '#ffdd00' }}>Prime</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="logo-actions">
            <input
              type="file"
              id="mainLogo"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'mainLogo')}
              style={{ display: 'none' }}
            />
            <label htmlFor="mainLogo" className="upload-btn" style={{
              background: '#2652a0',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-upload"></i>
              Upload New Logo
            </label>
            {previews.mainLogo && (
              <button 
                className="remove-btn"
                onClick={() => handleReset('mainLogo')}
                style={{
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginLeft: '10px',
                  fontSize: '12px'
                }}
              >
                <i className="fas fa-undo"></i>
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Blue Logo */}
        <div className="upload-section" style={{
          background: '#f8f9fa',
          borderRadius: '12px',
          padding: '25px',
          border: '2px solid #e9ecef',
          textAlign: 'center',
          transition: 'all 0.3s ease'
        }}>
          <h3 style={{
            color: '#2652a0',
            marginBottom: '15px',
            fontSize: '1.2rem',
            fontWeight: '600'
          }}>Blue Logo</h3>
          <p style={{
            color: '#6c757d',
            marginBottom: '20px',
            fontSize: '14px'
          }}>Used on light backgrounds and print materials</p>
          
          <div className="upload-area" style={{
            marginBottom: '20px'
          }}>
            <div className="upload-placeholder" style={{
              width: '150px',
              height: '80px',
              background: '#ffffff',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 15px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {previews.blueLogo || (logoSettings.blueLogo && logoSettings.blueLogo !== '/assets/logos/logo-fallback.svg') ? (
                <img 
                  src={previews.blueLogo || logoSettings.blueLogo} 
                  alt="Blue Logo Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <div style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#2652a0',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#ffdd00', marginRight: '5px' }}>〜</span>
                  <span style={{ color: '#2652a0' }}>River</span>
                  <span style={{ color: '#ffdd00' }}>Prime</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="logo-actions">
            <input
              type="file"
              id="blueLogo"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'blueLogo')}
              style={{ display: 'none' }}
            />
            <label htmlFor="blueLogo" className="upload-btn" style={{
              background: '#2652a0',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-upload"></i>
              Upload New Logo
            </label>
            {previews.blueLogo && (
              <button 
                className="remove-btn"
                onClick={() => handleReset('blueLogo')}
                style={{
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginLeft: '10px',
                  fontSize: '12px'
                }}
              >
                <i className="fas fa-undo"></i>
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Favicon */}
        <div className="upload-section" style={{
          background: '#f8f9fa',
          borderRadius: '12px',
          padding: '25px',
          border: '2px solid #e9ecef',
          textAlign: 'center',
          transition: 'all 0.3s ease'
        }}>
          <h3 style={{
            color: '#2652a0',
            marginBottom: '15px',
            fontSize: '1.2rem',
            fontWeight: '600'
          }}>Favicon</h3>
          <p style={{
            color: '#6c757d',
            marginBottom: '20px',
            fontSize: '14px'
          }}>Browser tab icon (32x32px recommended)</p>
          
          <div className="upload-area" style={{
            marginBottom: '20px'
          }}>
            <div className="upload-placeholder" style={{
              width: '60px',
              height: '60px',
              background: '#e9ecef',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 15px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {previews.favicon || (logoSettings.favicon && logoSettings.favicon !== '/favicon.ico') ? (
                <img 
                  src={previews.favicon || logoSettings.favicon} 
                  alt="Favicon Preview"
                  style={{
                    width: '32px',
                    height: '32px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <i className="fas fa-globe" style={{
                  fontSize: '24px',
                  color: '#6c757d'
                }}></i>
              )}
            </div>
          </div>
          
          <div className="logo-actions">
            <input
              type="file"
              id="favicon"
              accept="image/*,.ico"
              onChange={(e) => handleFileUpload(e, 'favicon')}
              style={{ display: 'none' }}
            />
            <label htmlFor="favicon" className="upload-btn" style={{
              background: '#2652a0',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-upload"></i>
              Upload New Favicon
            </label>
            {previews.favicon && (
              <button 
                className="remove-btn"
                onClick={() => handleReset('favicon')}
                style={{
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginLeft: '10px',
                  fontSize: '12px'
                }}
              >
                <i className="fas fa-undo"></i>
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Logo Preview Section */}
      <div className="logo-preview-section" style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '30px',
        border: '2px solid #e9ecef'
      }}>
        <h3 style={{
          color: '#2652a0',
          marginBottom: '15px',
          fontSize: '1.4rem',
          fontWeight: '600'
        }}>Website Preview</h3>
        <p style={{
          color: '#6c757d',
          marginBottom: '25px'
        }}>This is how your logo will appear on the website:</p>
        
        <div className="header-preview" style={{
          background: '#2652a0',
          padding: '20px 30px',
          borderRadius: '12px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(38, 82, 160, 0.2)'
        }}>
          <div className="logo-container" style={{
            marginRight: '30px'
          }}>
            {(previews.mainLogo || (logoSettings.mainLogo && logoSettings.mainLogo !== '/assets/logos/logo-fallback.svg')) ? (
              <img 
                src={previews.mainLogo || logoSettings.mainLogo} 
                alt="RiverPrime Logo Preview" 
                style={{
                  height: '45px',
                  maxWidth: '220px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="text-logo-preview" style={{ 
              display: (!previews.mainLogo && (!logoSettings.mainLogo || logoSettings.mainLogo === '/assets/logos/logo-fallback.svg')) ? 'flex' : 'none',
              alignItems: 'center',
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '26px',
              fontWeight: '700',
              color: '#ffffff'
            }}>
              <span style={{ color: '#ffdd00', marginRight: '8px' }}>〜</span>
              <span style={{ color: '#ffffff' }}>River</span>
              <span style={{ color: '#ffdd00' }}>Prime</span>
            </div>
          </div>
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ 
              color: '#ffdd00', 
              fontWeight: '600',
              fontSize: '16px'
            }}>
              Website Header Preview
            </span>
            <div style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px'
            }}>
              Navigation Menu
            </div>
          </div>
        </div>
        
        {(previews.mainLogo || previews.blueLogo || previews.favicon) && (
          <div className="preview-notice" style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '0',
            color: '#856404',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="fas fa-info-circle" style={{ 
              marginRight: '10px',
              fontSize: '18px'
            }}></i>
            <div>
              <strong>Preview:</strong> You have unsaved changes. Click "Save Changes & Apply to Website" below to apply them to your website.
            </div>
          </div>
        )}
      </div>

      <div className="section-actions" style={{
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        <button 
          className="save-btn" 
          onClick={handleSave}
          style={{
            background: 'linear-gradient(135deg, #ffdd00 0%, #ffc700 100%)',
            color: '#2652a0',
            border: 'none',
            padding: '15px 30px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 4px 12px rgba(255, 221, 0, 0.3)',
            marginRight: '15px'
          }}
        >
          <i className="fas fa-save"></i>
          Save Changes & Apply to Website
        </button>
        
        <button 
          onClick={() => {
            const adminLogos = localStorage.getItem('adminLogos');
            const adminLogoSettings = localStorage.getItem('adminLogoSettings');
            console.log('=== LOGO DEBUG INFO ===');
            console.log('adminLogos:', adminLogos);
            console.log('adminLogoSettings:', adminLogoSettings);
            console.log('current logoSettings:', logoSettings);
            console.log('current previews:', previews);
            alert(`Debug Info (check console):\n\nadminLogos: ${adminLogos ? 'exists' : 'missing'}\nadminLogoSettings: ${adminLogoSettings ? 'exists' : 'missing'}\n\nCheck browser console for full details.`);
          }}
          style={{
            background: '#6c757d',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginRight: '10px'
          }}
        >
          <i className="fas fa-bug"></i>
          Debug Storage
        </button>
        
        <button 
          onClick={() => {
            if (window.confirm('This will clear all logo settings and reload the page. Continue?')) {
              localStorage.removeItem('adminLogos');
              localStorage.removeItem('adminLogoSettings');
              window.location.reload();
            }
          }}
          style={{
            background: '#dc3545',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className="fas fa-trash"></i>
          Clear All Logo Data
        </button>
      </div>

      <div className="logo-guidelines">
        <h3>Logo Guidelines</h3>
        <div className="guidelines-grid">
          <div className="guideline-item">
            <i className="fas fa-info-circle"></i>
            <div>
              <strong>Format:</strong> PNG or SVG recommended for logos, ICO for favicon
            </div>
          </div>
          <div className="guideline-item">
            <i className="fas fa-ruler"></i>
            <div>
              <strong>Size:</strong> Minimum 200px width for logos, 32x32px for favicon
            </div>
          </div>
          <div className="guideline-item">
            <i className="fas fa-file-archive"></i>
            <div>
              <strong>File Size:</strong> Keep under 2MB for optimal loading speed
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoManagement;